/*eslint @typescript-eslint/no-empty-function: "off"*/
import React, { useContext, useEffect, useState } from 'react';
import {
  connect,
  MapDispatchToProps,
  MapStateToProps,
  useSelector,
} from 'react-redux';
import { RouteComponentProps, navigate } from '@reach/router';
import initFirebase from 'utils/firebase.js';
import {
  getCompanyManagers,
  getCompanyManagersFn,
  requireDocument,
  requireDocumentInFolder,
  requireDocumentInFolderFn,
} from 'store/providers/providerSlice';
import { display } from 'components/ErrorView/ErrorDialog';
import protectedRoute from 'components/protectedRoute';
import LogoLoading from 'components/LogoLoading/LogoLoading';
import AccessDenied from '../AccessDenied';
import { getRedirectToLoginLocation } from 'utils/happyFunctions';
import { IProviders } from 'store/providers/providerTypes';
import { appDispatch, IUsers, RootState } from 'store/store';
import { ISearchParams } from 'store/entity/types';
import { searchTasks, searchTasksFn } from 'store/tasks/tasksSlice';
import {
  requireNewMessages,
  requireNewMessagesFn,
} from 'store/messages/messagesSlice';
import { changeStateOFSideBar } from 'store/app/appSlice';
import {
  getCompanyHandler,
  getCompanyHandlerFn,
  getMyCompanies,
  getMyCompaniesFn,
} from 'store/companies/companiesSlice';
import {
  IConversationState,
  requireConversation,
  requireConversationFn,
} from 'store/conversation/conversationSlice';
import {
  IThisUserState,
  submitSingleDevice,
  submitSingleDeviceFn,
} from 'store/thisUser/thisUserSlice';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import {
  isCompanyOwner,
  isCompanySupport,
} from 'store/companies/companiesUtils';
import {
  ICompany,
  ICompanyProviders,
  TProjects,
} from 'store/companies/companiesTypes';
import { IUser } from 'store/users/usersType';
import { OpenItemsContext } from '../../store/context/OpenItemsContext';
import { parse } from 'query-string';

type StateToProps = {
  companyName: string;
  conversations: IConversationState;
  hasCompany: boolean;
  companyLoaded: boolean;
  inboxId: number;
  isSideBarCollapsed: boolean;
  projects: TProjects;
  providers: IProviders;
  relatedProviders: ICompanyProviders;
  thisUser: IThisUserState;
  user: IUser;
  isUserCompanyAdmin: boolean;
  isSupport: boolean;
  memberUserIds: number[];
  users: IUsers['map'];
  company: ICompany;
};
type DispatchProps = {
  changeStateOfSideBar: (isSideBarCollapsed: boolean) => void;
  getCompany: (companyId: number) => ReturnType<typeof getCompanyHandlerFn>;
  getCompanyManagers: (
    companyId: number
  ) => ReturnType<typeof getCompanyManagersFn>;
  getMyCompanies: () => ReturnType<typeof getMyCompaniesFn>;
  requireSeveralUser: (
    userIds: number[]
  ) => ReturnType<typeof requireSeveralUserFn>;
  requireConversation: (
    inboxId: number
  ) => ReturnType<typeof requireConversationFn>;
  requireNewMessages: (
    conversationId: number,
    messageId: number,
    conversationToken?: string
  ) => ReturnType<typeof requireNewMessagesFn>;
  searchTasks: (params: ISearchParams) => ReturnType<typeof searchTasksFn>;
  submitSingleDevice: (
    name: string,
    token: string
  ) => ReturnType<typeof submitSingleDeviceFn>;
  getDocumentsInFolder: (
    folderId: number,
    providerId: number
  ) => ReturnType<typeof requireDocumentInFolderFn>;
};
type OtherTypes = {
  children: React.ReactNode;
};
type ParamsProps = {
  companyId: string;
};
type OwnProps = RouteComponentProps<ParamsProps>;
type ComponentProps = StateToProps & DispatchProps & OwnProps & OtherTypes;

const TasksManagement: React.FC<ComponentProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const companies = useSelector((state: RootState) => state.companies.data);
  const providers = useSelector((state: RootState) => state.providers.items);
  const cntx = useContext(OpenItemsContext);

  const init = async () => {
    const {
      companyId,
      getCompany,
      requireSeveralUser,
      getMyCompanies,
      thisUser,
      companyLoaded,
    } = props;

    if (companyId) {
      const parsed = parse(window.location!.search);
      if (
        (!companyLoaded && (thisUser!.access_token?.length || parsed?.tt)) ??
        0 > 0
      ) {
        setLoading(true);
        getCompany(Number(companyId))
          .then(() => {
            setLoading(false);
          })
          .catch((error) => {
            if (error.code === 403) {
              if (!thisUser || !thisUser.userId) {
                navigate(getRedirectToLoginLocation());
              } else setAccessDenied(true);
              setLoading(false);
            } else display(error);
          });
      } else {
        const toLoadCompanyMembers = props.memberUserIds.filter(
          (it) => !props.users[it]
        );
        if (toLoadCompanyMembers.length > 0) setLoading(true);
        const promise =
          toLoadCompanyMembers.length > 0
            ? requireSeveralUser(toLoadCompanyMembers)
            : Promise.resolve();

        promise.then(() => {
          setLoading(false);
        });
      }
    } else {
      setLoading(true);
      getMyCompanies()
        .then(async () => {
          setLoading(false);
        })
        .catch((err: any) => {
          display(err);
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    const {
      requireConversation,
      requireNewMessages,
      submitSingleDevice,
      thisUser,
    } = props;

    init();

    initFirebase(
      thisUser,
      submitSingleDevice,
      requireConversation,
      requireNewMessages
    );
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const itemId = params.get('id');

    if (params.has('id')) {
      if (Object.keys(companies).includes(itemId as string)) {
        const providerListIds = Object.keys(
          Object.values(companies?.[itemId ?? ''].relatedProviders)
        );

        providerListIds.map((providerId: string) => {
          if (
            Object.keys(providers[Number(providerId)].items).includes(
              itemId as string
            )
          ) {
            cntx.changeOpenItems(Number(itemId), Number(providerId));
          }
        });
      } else {
        appDispatch(requireDocument({ documentId: Number(itemId) })).then(
          (res: any) => {
            cntx.changeOpenItems(Number(itemId), res.item.providerId);
          }
        );
      }
    } else {
      cntx.handleMinimizeAllItems();
    }
  }, [window.location.search]);

  if (!cntx) {
    return <></>;
  }

  if (loading || !props.companyLoaded) {
    return <LogoLoading />;
  }

  const { children } = props;

  if (accessDenied) {
    return <AccessDenied />;
  }
  return <>{children}</>;
};

const mapStateToProps: MapStateToProps<StateToProps, OwnProps, RootState> = (
  state,
  props
) => {
  const thisUser = state.thisUser;
  const userId = Number(thisUser.userId);
  const users = state.users.map;
  const user = state.users.map[userId];
  const companyId = Number(props.companyId);
  const companies = state.companies;
  const company = companies.data[companyId];
  const companyLoaded =
    !companyId || (company && company?.status === 'loaded' && !company?.stub);
  const companyName = company?.name;
  const memberUserIds = Object.values(company?.members ?? {}).map(
    (it) => it.userId
  );
  const isUserCompanyAdmin = Boolean(
    (company?.admins || []).find((item: number) => {
      return item === userId;
    })
  );
  let hasCompany = Object.keys(companies.data).length > 0;
  if (!props.companyId) {
    hasCompany = false;
  }
  const projects = company?.projects;
  const conversations = state.conversations;
  const inboxId = company?.members?.[userId]?.conversationInboxId;
  const relatedProviders = company?.relatedProviders;
  const providers = state.providers;
  const isSideBarCollapsed = state.app.UI.sidebar.isSideBarCollapsed;
  const isSupport =
    (company && isCompanySupport(thisUser, company)) ||
    isCompanyOwner(thisUser, company);

  return {
    companyName,
    companyLoaded,
    conversations,
    hasCompany,
    inboxId,
    isSideBarCollapsed,
    projects,
    relatedProviders,
    providers,
    thisUser,
    user,
    memberUserIds,
    isUserCompanyAdmin,
    isSupport,
    users,
    company,
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = () => ({
  changeStateOfSideBar: (isSideBarCollapsed: boolean) =>
    appDispatch(changeStateOFSideBar({ isCollapsed: isSideBarCollapsed })),
  getCompany: (companyId: number) =>
    appDispatch(getCompanyHandler({ companyId, projectStub: false })),
  getCompanyManagers: (companyId: number) =>
    appDispatch(getCompanyManagers({ companyId })),
  getMyCompanies: () => appDispatch(getMyCompanies({})),
  requireConversation: (inboxId: number) =>
    appDispatch(requireConversation({ conversationId: inboxId })),
  requireNewMessages: (
    conversationId: number,
    messageId: number,
    conversationToken?: string
  ) =>
    appDispatch(
      requireNewMessages({
        conversationId,
        messageId,
        conversationToken: conversationToken!,
      })
    ),
  searchTasks: (params: ISearchParams) => appDispatch(searchTasks({ params })),
  submitSingleDevice: (name: string, token: string) =>
    appDispatch(submitSingleDevice({ name, token })),
  requireSeveralUser: (userIds: number[]) =>
    appDispatch(requireSeveralUser({ usersList: userIds })),
  getDocumentsInFolder: (folderId: number, providerId: number) =>
    appDispatch(requireDocumentInFolder({ folderId, providerId })),
});

export default protectedRoute(
  connect(mapStateToProps, mapDispatchToProps)(TasksManagement)
);
